"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";

export type AppDialogProps = {
  /** 트리거 요소 */
  trigger?: React.ReactNode;

  /** 툴팁 내용 (지정 시 trigger를 Tooltip으로 감쌈) */
  tooltip?: string;

  /** 외부 제어 여부 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  /** 상단 아이콘 */
  icon?: React.ReactNode;

  /** 다이얼로그 제목 */
  title: string | React.ReactNode;

  /** 다이얼로그 제목 sr-only */
  titleSrOnly?: boolean;

  /** 설명 */
  description: string | React.ReactNode;

  /** 설명 sr-only */
  descriptionSrOnly?: boolean;

  /** 하단 확인 버튼 텍스트 (기본값: 확인) */
  confirmLabel?: string;

  /** 확인 버튼 클릭 시 실행 */
  onConfirm?: () => void;

  /**
   * footer 전체를 직접 구성하고 싶을 경우
   * `DialogFooter`를 포함하는 JSX 전달
   * 지정할 경우 `confirmLabel` / `onConfirm` 무시
   */
  footer?: React.ReactNode;

  /** 다이얼로그 본문 콘텐츠 */
  children: React.ReactNode;

  /** 다이얼로그 최대 너비 (기본값: sm:max-w-[480px]) */
  maxWidth?: string;

  showCloseButton?: boolean;

  /** ESC 키로 닫히는 것을 막을지 여부 */
  preventEscapeKeyDown?: boolean;

  /** 배경 클릭으로 닫히는 것을 막을지 여부 */
  preventInteractOutside?: boolean;

  /** DialogHeader 추가 className */
  headerClassName?: string;

  /** DialogTitle 추가 className */
  titleClassName?: string;
};

export function AppDialog({
  trigger,
  tooltip,
  open,
  onOpenChange,
  showCloseButton = true,
  icon,
  title = "",
  titleSrOnly = false,
  description,
  descriptionSrOnly = true,
  confirmLabel = "확인",
  onConfirm,
  footer,
  children,
  maxWidth,
  preventEscapeKeyDown = false,
  preventInteractOutside = false,
  headerClassName,
  titleClassName,
}: AppDialogProps) {
  const showDefaultFooter = footer === undefined && (onConfirm || confirmLabel);

  const triggerElement = trigger ? (
    tooltip ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>{trigger}</DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    ) : (
      <DialogTrigger asChild>{trigger}</DialogTrigger>
    )
  ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {triggerElement}
      <DialogContent
        className={cn("p-6", maxWidth)}
        showCloseButton={showCloseButton}
        onInteractOutside={preventInteractOutside ? e => e.preventDefault() : undefined}
        onEscapeKeyDown={preventEscapeKeyDown ? e => e.preventDefault() : undefined}
      >
        {/* ──────────────── Header ──────────────── */}
        {title === "" ? (
          <></>
        ) : (
          <DialogHeader className={cn("flex flex-col gap-3 text-left", headerClassName)}>
            {icon && (
              <div
                aria-hidden
                className="bg-primary/10 text-primary flex size-12 items-center justify-center rounded-full"
              >
                {icon}
              </div>
            )}
            <div>
              <DialogTitle
                className={cn(
                  "text-lg leading-snug font-semibold",
                  titleSrOnly && "sr-only",
                  titleClassName
                )}
              >
                {title}
              </DialogTitle>
              <DialogDescription className={cn(descriptionSrOnly ? "sr-only" : "text-neutral-600")}>
                {description}
              </DialogDescription>
            </div>
          </DialogHeader>
        )}

        {/* ──────────────── Body ──────────────── */}
        {children}

        {/* ──────────────── Footer ──────────────── */}
        {footer !== undefined ? (
          footer
        ) : showDefaultFooter ? (
          <DialogFooter className="flex w-full justify-end gap-2">
            <DialogClose asChild>
              <Button
                onClick={onConfirm}
                className={cn("min-w-[120px]", !onConfirm ? "w-full" : "")}
              >
                {confirmLabel}
              </Button>
            </DialogClose>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
